import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Plus, Mail, Send, Clock, CheckCircle2, XCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { listCampaigns, cancelCampaign, deleteCampaign } from '@/lib/admin/marketing-campaigns.functions';
import { toast } from 'sonner';

export const Route = createFileRoute('/_admin/admin/marketing/campaigns/')({
  component: CampaignsDashboard,
});

function CampaignsDashboard() {
  const { data: campaigns, isLoading, refetch } = useQuery({
    queryKey: ['admin', 'marketing', 'campaigns'],
    queryFn: () => listCampaigns(),
  });

  const handleCancel = async (id: string) => {
    if (!confirm('Are you sure you want to cancel this campaign?')) return;
    try {
      await cancelCampaign({ data: { id } });
      toast.success('Campaign cancelled');
      refetch();
    } catch (error: any) {
      toast.error(error.message || 'Failed to cancel campaign');
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Permanently delete the campaign "${name}"? This cannot be undone.`)) return;
    try {
      await deleteCampaign({ data: { id } });
      toast.success('Campaign deleted');
      refetch();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete campaign');
    }
  };

  const stats = {
    total: campaigns?.length || 0,
    draft: campaigns?.filter(c => c.status === 'draft').length || 0,
    scheduled: campaigns?.filter(c => c.status === 'scheduled').length || 0,
    sent: campaigns?.filter(c => c.status === 'sent').length || 0,
    failed: campaigns?.filter(c => c.status === 'failed').length || 0,
    totalSent: campaigns?.reduce((acc, c) => acc + (c.total_sent || 0), 0) || 0,
    totalDelivered: campaigns?.reduce((acc, c) => acc + (c.total_delivered || 0), 0) || 0,
    totalOpened: campaigns?.reduce((acc, c) => acc + (c.total_opened || 0), 0) || 0,
    totalClicked: campaigns?.reduce((acc, c) => acc + (c.total_clicked || 0), 0) || 0,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Email Campaigns</h1>
          <p className="text-muted-foreground">Create and manage your marketing email campaigns.</p>
        </div>
        <Button asChild>
          <Link to="/admin/marketing/campaigns/new" className="inline-flex items-center">
            <Plus className="mr-2 h-4 w-4" />
            New Campaign
          </Link>
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Campaigns</CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Sent</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.sent}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Draft/Scheduled</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.draft + stats.scheduled}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Open Rate</CardTitle>
            <Send className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.totalSent > 0 
                ? `${Math.round((stats.totalOpened / stats.totalSent) * 100)}%` 
                : '0%'}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Campaigns</CardTitle>
          <CardDescription>A list of your marketing campaigns and their current status.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-10 text-center text-muted-foreground">Loading campaigns...</div>
          ) : !campaigns || campaigns.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              No campaigns found. Create your first one to get started.
            </div>
          ) : (
            <div className="relative overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b text-muted-foreground uppercase text-[11px] font-semibold">
                  <tr>
                    <th className="pb-3 pl-2">Name</th>
                    <th className="pb-3">Type</th>
                    <th className="pb-3">Status</th>
                    <th className="pb-3">Audience</th>
                    <th className="pb-3 text-right">Sent</th>
                    <th className="pb-3 text-right">Open Rate</th>
                    <th className="pb-3 text-right">Date</th>
                    <th className="pb-3 text-right pr-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {campaigns.map((campaign) => (
                    <tr key={campaign.id} className="group hover:bg-muted/40">
                      <td className="py-3 pl-2">
                        <Link 
                          to="/admin/marketing/campaigns/new" 
                          search={{ id: campaign.id }}
                          className="hover:underline"
                        >
                          <span className="font-medium text-foreground">
                            {campaign.name}
                          </span>
                        </Link>
                        <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                          {campaign.subject}
                        </div>
                      </td>
                      <td className="py-3 capitalize">
                        {campaign.type.replace('_', ' ')}
                      </td>
                      <td className="py-3">
                        <StatusBadge status={campaign.status} />
                      </td>
                      <td className="py-3 capitalize">
                        {campaign.audience_type.replace('_', ' ')}
                      </td>
                      <td className="py-3 text-right">
                        {campaign.total_sent || 0}
                      </td>
                      <td className="py-3 text-right">
                        {campaign.total_sent && campaign.total_sent > 0 
                          ? `${Math.round(((campaign.total_opened || 0) / campaign.total_sent) * 100)}%` 
                          : '0%'}
                      </td>
                      <td className="py-3 text-right text-muted-foreground">
                        {format(new Date(campaign.created_at), 'dd MMM yyyy')}
                      </td>
                      <td className="py-3 text-right pr-2">
                        {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-destructive"
                            onClick={() => handleCancel(campaign.id)}
                            title="Cancel Campaign"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        )}
                        {(campaign.status === 'draft' || campaign.status === 'cancelled') && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            onClick={() => handleDelete(campaign.id, campaign.name)}
                            title="Delete Campaign"
                            aria-label="Delete Campaign"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'draft':
      return <Badge variant="secondary" className="bg-slate-100 text-slate-700 hover:bg-slate-100 border-none">Draft</Badge>;
    case 'scheduled':
      return <Badge variant="outline" className="text-blue-600 border-blue-200">Scheduled</Badge>;
    case 'sending':
      return <Badge className="bg-blue-600 hover:bg-blue-700">Sending</Badge>;
    case 'sent':
      return <Badge className="bg-green-600 hover:bg-green-700">Sent</Badge>;
    case 'failed':
      return <Badge variant="destructive">Failed</Badge>;
    case 'cancelled':
      return <Badge variant="secondary">Cancelled</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}
